import { ACTIONS, applyAction, computeScore, getLegalMoves } from '../engine';
import type { Action, Card, GameState } from '../engine';
import type { TeamId } from '../engine/types';
import type { MoveOption } from '../engine/moves';
import { getActiveAiWeights, type AiLearningWeights } from './aiLearning';

export type AiDifficulty = 'EASY' | 'NORMAL' | 'HARD';
export type ChooseAiOptions = {
  // When true, AI may use perfect-information search (LOCAL play only).
  perfectInfo?: boolean;
  // When true, skip expensive lookahead (used for rollouts / background sim).
  fast?: boolean;
};

function uniq<T>(items: T[]): T[] {
  return Array.from(new Set(items));
}

function pickRandom<T>(items: T[], rng: () => number): T {
  return items[Math.floor(rng() * items.length)];
}

function getPlayerTeamId(state: GameState, playerId: string): TeamId {
  const player = state.players.find((entry) => entry.id === playerId);
  if (!player) throw new Error(`Unknown player ${playerId}`);
  return player.teamId;
}

function getOpponentTeamIds(state: GameState, teamId: TeamId): TeamId[] {
  const teams = uniq(state.players.map((player) => player.teamId));
  return teams.filter((id) => id !== teamId);
}

function isSpade(card: Pick<Card, 'suit'>): boolean {
  return card.suit === 'S';
}

function isTenDiamonds(card: Pick<Card, 'rank' | 'suit'>): boolean {
  return card.rank === '10' && card.suit === 'D';
}

function isAce(card: Pick<Card, 'rank'>): boolean {
  return card.rank === 'A';
}

function isTwoSpades(card: Pick<Card, 'rank' | 'suit'>): boolean {
  return card.rank === '2' && card.suit === 'S';
}

function isRoyal(card: Pick<Card, 'rank'>): boolean {
  return card.rank === 'J' || card.rank === 'Q' || card.rank === 'K';
}

function isValuableDropCard(card: Card): boolean {
  const v = numericRankValue(card.rank);
  const highNumeric = v !== null && v >= 7 && v <= 10;
  // Treat these as "do not drop" in HARD unless forced:
  // - scoring/bonus cards: Aces, 10♦, 2♠
  // - spades (spade bonus potential)
  // - high numerics (often useful for takes/builds)
  return isAce(card) || isTenDiamonds(card) || isTwoSpades(card) || isSpade(card) || highNumeric || isRoyal(card);
}

function countRoyalRank(cards: Card[], rank: Card['rank']): number {
  if (rank !== 'J' && rank !== 'Q' && rank !== 'K') return 0;
  return cards.filter((card) => card.rank === rank).length;
}

function countNumericValue(cards: Card[], value: number): number {
  return cards.reduce((sum, card) => {
    const v = numericRankValue(card.rank);
    return sum + (v === value ? 1 : 0);
  }, 0);
}

function numericRankValue(rank: string): number | null {
  if (rank === 'A') return 1;
  if (rank === 'J' || rank === 'Q' || rank === 'K') return null;
  return Number(rank);
}

function totalRemainingCards(state: GameState): number {
  return state.deck.length + Object.values(state.hands).reduce((sum, hand) => sum + hand.length, 0);
}

function isLastRound(state: GameState): boolean {
  return state.meta.roundIndex >= state.meta.totalRounds - 1;
}

function sweepableTableCount(state: GameState): number {
  const buildCards = state.builds.reduce((sum, build) => sum + build.cards.length, 0);
  const stayCards = state.stays.reduce((sum, stay) => sum + stay.cards.length, 0);
  return state.tableLoose.length + buildCards + stayCards;
}

function totalHandCards(state: GameState): number {
  return Object.values(state.hands).reduce((sum, hand) => sum + hand.length, 0);
}

function evaluateTeamCollections(state: GameState, teamId: TeamId): number {
  const cards = state.collections[teamId]?.cards || [];
  const score = computeScore(cards).total;
  return score * 100 + cards.length * 2;
}

function evaluatePosition(state: GameState, perspectiveTeamId: TeamId): number {
  const oppTeams = getOpponentTeamIds(state, perspectiveTeamId);
  const oppValue = oppTeams.reduce((sum, teamId) => sum + evaluateTeamCollections(state, teamId), 0);
  return evaluateTeamCollections(state, perspectiveTeamId) - oppValue;
}

function stableMoveKey(move: Action): string {
  return JSON.stringify(move);
}

function getPerspectiveTeamId(state: GameState, perspectivePlayerId: string): TeamId {
  return getPlayerTeamId(state, perspectivePlayerId);
}

function evaluateHandFeatures(state: GameState, perspectivePlayerId: string): number {
  const hand = state.hands[perspectivePlayerId] || [];
  const has10d = hand.some(isTenDiamonds);
  const has2s = hand.some(isTwoSpades);
  const spadeCount = hand.filter(isSpade).length;
  const royalCount = hand.filter(isRoyal).length;
  const highCount = hand.filter((card) => {
    const v = numericRankValue(card.rank);
    return v !== null && v >= 7 && v <= 10;
  }).length;

  const remaining = totalRemainingCards(state);
  const lateGame = remaining <= 12;
  const royalWeight = lateGame ? 4 : 1;

  return (
    (has10d ? 20 : 0) +
    (has2s ? 12 : 0) +
    spadeCount * 1.5 +
    royalCount * royalWeight +
    highCount * 1.0
  );
}

function evaluateState(state: GameState, perspectivePlayerId: string, difficulty: AiDifficulty): number {
  const teamId = getPerspectiveTeamId(state, perspectivePlayerId);
  const position = evaluatePosition(state, teamId);
  if (difficulty !== 'HARD') return position;
  const hand = evaluateHandFeatures(state, perspectivePlayerId);
  return position + hand;
}

function pointsDiff(state: GameState, perspectiveTeamId: TeamId): number {
  const teams = uniq(state.players.map((p) => p.teamId));
  const scores = state.meta.scores || {};
  const getPoints = (teamId: TeamId): number => {
    const entry: any = (scores as any)[teamId];
    if (entry && typeof entry.total === 'number') return entry.total;
    const cards = state.collections[teamId]?.cards || [];
    return computeScore(cards).total;
  };
  const my = getPoints(perspectiveTeamId);
  const opp = teams.filter((t) => t !== perspectiveTeamId).reduce((sum, t) => sum + getPoints(t), 0);
  return my - opp;
}

function cardsDiff(state: GameState, perspectiveTeamId: TeamId): number {
  const teams = uniq(state.players.map((p) => p.teamId));
  const myCards = (state.collections[perspectiveTeamId]?.cards || []).length;
  const oppCards = teams.filter((t) => t !== perspectiveTeamId).reduce((sum, t) => sum + (state.collections[t]?.cards || []).length, 0);
  return myCards - oppCards;
}

function rolloutScoreEnd(state: GameState, perspectiveTeamId: TeamId): number {
  // Primary objective: points. Secondary: card count (for 26+ bonus / pressure).
  return pointsDiff(state, perspectiveTeamId) * 1000 + cardsDiff(state, perspectiveTeamId) * 2;
}

function simulateToEnd(
  start: GameState,
  perspectivePlayerId: string,
  rng: () => number,
  difficulty: AiDifficulty
): number {
  const perspectiveTeamId = getPerspectiveTeamId(start, perspectivePlayerId);
  let state = start;
  let steps = 0;
  const maxSteps = 600;

  while (steps < maxSteps) {
    steps += 1;
    if (state.phase === 'gameOver') {
      return rolloutScoreEnd(state, perspectiveTeamId);
    }
    if (state.phase === 'betweenRounds') {
      state = applyAction(state, { type: ACTIONS.START_ROUND, roundIndex: state.meta.roundIndex } as any);
      continue;
    }
    if (state.phase !== 'playing') {
      // scoring -> gameOver happens inside reducer; just evaluate current collections.
      return rolloutScoreEnd(state, perspectiveTeamId);
    }
    const pid = state.currentPlayerId;
    if (!pid) return rolloutScoreEnd(state, perspectiveTeamId);
    const action = chooseAiAction(state, pid, difficulty, rng, { perfectInfo: false, fast: true });
    state = applyAction(state, action);
  }

  return rolloutScoreEnd(state, perspectiveTeamId);
}

function chooseByPointRollouts(
  state: GameState,
  playerId: string,
  moves: MoveOption[],
  rng: () => number,
  difficulty: AiDifficulty,
  weights: AiLearningWeights
): Action {
  const preferNonSpadeShapes = preferNonSpadeForEquivalentMoves(state, playerId, moves);
  const rootScored = moves.map((move) => ({ move, score: scoreMove(state, playerId, move, difficulty, preferNonSpadeShapes, weights) }));
  const bestRoot = Math.max(...rootScored.map((entry) => entry.score));
  const window = 10;
  const candidates = rootScored
    .filter((entry) => entry.score >= bestRoot - window)
    .sort((a, b) => b.score - a.score)
    .slice(0, 4)
    .map((entry) => entry.move);

  const remaining = totalRemainingCards(state);
  const rollouts = remaining <= 14 ? 4 : remaining <= 24 ? 3 : 2;

  const scored = candidates.map((move) => {
    const next = applyAction(state, move);
    let sum = 0;
    for (let i = 0; i < rollouts; i += 1) {
      // add tiny randomness so repeated rollouts explore different lines
      const jitter = () => (rng() + i * 0.17) % 1;
      sum += simulateToEnd(next, playerId, jitter, 'HARD');
    }
    return { move, score: sum / rollouts };
  });

  const best = Math.max(...scored.map((entry) => entry.score));
  const bestMoves = scored.filter((entry) => entry.score === best).map((entry) => entry.move);
  return pickRandom(bestMoves, rng);
}

function getPlayedCardId(move: MoveOption): string | null {
  const anyMove = move as Record<string, any>;
  if (typeof anyMove.cardId === 'string') return anyMove.cardId;
  if (typeof anyMove.cardIdFromHand === 'string') return anyMove.cardIdFromHand;
  if (typeof anyMove.playedCardIdFromHand === 'string') return anyMove.playedCardIdFromHand;
  if (typeof anyMove.cardIdFromHand1 === 'string') return anyMove.cardIdFromHand1;
  return null;
}

function getPlayedCardFromMove(state: GameState, playerId: string, move: MoveOption) {
  const playedId = getPlayedCardId(move);
  if (!playedId) return null;
  return (state.hands[playerId] || []).find((card) => card.id === playedId) || null;
}

function findNewCollectionCards(prev: GameState, next: GameState, teamId: TeamId): Card[] {
  const prevCards = prev.collections[teamId]?.cards || [];
  const nextCards = next.collections[teamId]?.cards || [];
  if (nextCards.length <= prevCards.length) return [];
  return nextCards.slice(prevCards.length);
}

function moveShapeKey(move: MoveOption): string {
  const clone: Record<string, any> = { ...(move as any) };
  delete clone.cardId;
  delete clone.cardIdFromHand;
  delete clone.playedCardIdFromHand;
  delete clone.cardIdFromHand1;
  delete clone.cardIdFromHand2;
  delete clone.label;
  return JSON.stringify(clone);
}

function preferNonSpadeForEquivalentMoves(state: GameState, playerId: string, moves: MoveOption[]): Map<string, boolean> {
  const byShape: Record<string, { hasSpade: boolean; hasNonSpade: boolean }> = {};
  moves.forEach((move) => {
    const key = moveShapeKey(move);
    if (!byShape[key]) byShape[key] = { hasSpade: false, hasNonSpade: false };
    const played = getPlayedCardFromMove(state, playerId, move);
    if (!played) return;
    if (isSpade(played)) byShape[key].hasSpade = true;
    else byShape[key].hasNonSpade = true;
  });
  const penaltyShapes = new Map<string, boolean>();
  Object.entries(byShape).forEach(([key, entry]) => {
    penaltyShapes.set(key, entry.hasSpade && entry.hasNonSpade);
  });
  return penaltyShapes;
}

function canAvoidTwoSpades(state: GameState, playerId: string, moves: MoveOption[]): boolean {
  return moves.some((move) => {
    const played = getPlayedCardFromMove(state, playerId, move);
    return !played || !isTwoSpades(played);
  });
}

function opponentCanTakeDroppedCard(next: GameState, droppedCardId: string, droppingPlayerId: string): boolean {
  if (next.phase !== 'playing') return false;
  const opponentId = next.currentPlayerId;
  if (!opponentId || opponentId === droppingPlayerId) return false;

  const replyMoves = getLegalMoves(next, opponentId);
  return replyMoves.some((move) => {
    if (move.type === ACTIONS.TAKE_SINGLE) {
      const target = (move as any).target as { cardIdOnTable?: string } | undefined;
      return target?.cardIdOnTable === droppedCardId;
    }
    if (move.type === ACTIONS.TAKE_SUBSET) {
      const target = (move as any).target as { cardIdsOnTable?: string[] } | undefined;
      return Boolean(target?.cardIdsOnTable?.includes(droppedCardId));
    }
    return false;
  });
}

function hasOpponentTopRoyalPair(state: GameState, perspectivePlayerId: string, rank: Card['rank']): boolean {
  if (rank !== 'J' && rank !== 'Q' && rank !== 'K') return false;
  const myTeamId = getPlayerTeamId(state, perspectivePlayerId);
  return Object.entries(state.collections)
    .filter(([teamId]) => teamId !== myTeamId)
    .some(([, stack]) => {
      const cards = stack.cards || [];
      if (cards.length < 2) return false;
      const top = cards[cards.length - 1];
      const second = cards[cards.length - 2];
      return Boolean(top && second && top.rank === rank && second.rank === rank);
    });
}

function worstOpponentReplyScoreForUs(
  nextState: GameState,
  currentPlayerId: string,
  difficulty: AiDifficulty,
  rng: () => number
): number | null {
  if (nextState.phase !== 'playing') return null;
  const oppId = nextState.currentPlayerId;
  if (!oppId || oppId === currentPlayerId) return null;

  const oppMovesAll = getLegalMoves(nextState, oppId);
  if (oppMovesAll.length === 0) return null;

  // Only consider replies that look most damaging for us.
  const oppCandidates = oppMovesAll
    .map((oppMove) => ({ oppMove, score: evaluateState(applyAction(nextState, oppMove), currentPlayerId, difficulty) }))
    .sort((a, b) => a.score - b.score)
    .slice(0, 10)
    .map((entry) => entry.oppMove);

  // Add tiny noise so equal lines don't always pick the first move.
  return Math.min(
    ...oppCandidates.map((oppMove) => evaluateState(applyAction(nextState, oppMove), currentPlayerId, difficulty) + rng() * 0.001)
  );
}

function isForcedToDrop(state: GameState, playerId: string): boolean {
  const legal = getLegalMoves(state, playerId);
  if (legal.length === 0) return false;
  return legal.every((move) => move.type === ACTIONS.DROP);
}

const DEFAULT_AI_WEIGHTS: AiLearningWeights = {
  protectPointsMult: 1,
  feedBuildAvoidMult: 1,
  dominanceBonusMult: 1,
  takeBiasMult: 1,
  avoidDropWhenTakeAvailableMult: 1,
  avoidLowValuePileTakeMult: 1,
  saveRoyalForLastMult: 1,
  avoidSingleAnchorBuildMult: 1,
  avoidEarlyHighBuildMult: 1,
  preferRoyalCoverMult: 1,
};

function moveSpecificPenalty(
  state: GameState,
  playerId: string,
  move: MoveOption,
  next: GameState,
  difficulty: AiDifficulty,
  weights: AiLearningWeights = DEFAULT_AI_WEIGHTS
): number {
  if (difficulty !== 'HARD') return 0;

  const played = getPlayedCardFromMove(state, playerId, move);
  if (!played) return 0;

  let penalty = 0;

  const remaining = totalRemainingCards(state);
  const lateGame = remaining <= 10;
  const lastRound = isLastRound(state);
  const endgame = lastRound && (lateGame || totalHandCards(state) <= 8);
  const playedNumeric = numericRankValue(played.rank);
  const legalNow = getLegalMoves(state, playerId);
  const takeAvailable = legalNow.some(
    (m) =>
      m.type === ACTIONS.TAKE_SINGLE ||
      m.type === ACTIONS.TAKE_SUBSET ||
      m.type === ACTIONS.TAKE_PILE ||
      m.type === ACTIONS.TAKE_SINGLE_ADD_TO_BUILD ||
      m.type === ACTIONS.TAKE_SINGLE_ADD_TO_STAY ||
      m.type === ACTIONS.TAKE_SUBSET_ADD_TO_BUILD ||
      m.type === ACTIONS.TAKE_SUBSET_ADD_TO_STAY ||
      m.type === ACTIONS.TAKE_PILE_ADD_TO_BUILD ||
      m.type === ACTIONS.TAKE_PILE_ADD_TO_STAY
  );
  const teamId = getPlayerTeamId(state, playerId);

  // 0) Don't feed obvious takes: if we drop a card we know the opponent can immediately take,
  // avoid it unless we have no alternative but to drop.
  if (move.type === ACTIONS.DROP && !isForcedToDrop(state, playerId)) {
    const canTake = opponentCanTakeDroppedCard(next, played.id, playerId);
    if (canTake) {
      // Big baseline penalty; extra in late game (turns are scarce).
      penalty += (85 + (lateGame ? 35 : 0) + (lastRound ? 25 : 0)) * weights.takeBiasMult;
    }
  }

  // 0.25) Prefer taking over dropping whenever any take is available (including subsets),
  // unless we're forced to drop.
  if (move.type === ACTIONS.DROP && takeAvailable && !isForcedToDrop(state, playerId)) {
    penalty += 120 * weights.avoidDropWhenTakeAvailableMult;
  }

  // 0.35) Avoid building a value when we only have 1 copy of the build value left in team hand after the move.
  // (Allowed, but risky unless forced/best.)
  if (
    move.type === ACTIONS.BUILD_CREATE ||
    move.type === ACTIONS.BUILD_UPDATE ||
    move.type === ACTIONS.BUILD_SOLID_FROM_TABLE ||
    move.type === ACTIONS.BUILD_SOLID_FROM_OPP_TOP ||
    move.type === ACTIONS.INCREASE_OPP_TOP_TO_MATCH
  ) {
    const nextTeamHand = state.players
      .filter((p) => p.teamId === teamId)
      .flatMap((p) => next.hands[p.id] || []);

    const buildValue = (() => {
      if ((move as any).type === ACTIONS.BUILD_CREATE) return (move as any).buildValue as number | null;
      if ((move as any).type === ACTIONS.BUILD_UPDATE) return (move as any).newValue as number | null;
      if ((move as any).type === ACTIONS.BUILD_SOLID_FROM_TABLE || (move as any).type === ACTIONS.BUILD_SOLID_FROM_OPP_TOP) {
        return playedNumeric;
      }
      // INCREASE_OPP_TOP_TO_MATCH: infer from next (a new build owned by us).
      const oursBefore = new Set(state.builds.filter((b) => b.ownerPlayerId === playerId).map((b) => b.id));
      const oursAfter = next.builds.filter((b) => b.ownerPlayerId === playerId && !oursBefore.has(b.id));
      return oursAfter.length ? oursAfter[0].value : null;
    })();

    if (buildValue !== null && buildValue >= 1 && buildValue <= 10) {
      const remainingInTeamHand = countNumericValue(nextTeamHand, buildValue);
      if (remainingInTeamHand === 1) {
        penalty += 75 * weights.avoidSingleAnchorBuildMult;
      }
    }
  }

  // 0.4) Avoid creating/increasing big builds (8-10) too early unless the table state supports it.
  {
    const remainingCards = totalRemainingCards(state);
    const earlyGame = remainingCards >= 22;
    const numericLoose = state.tableLoose.filter((c) => numericRankValue(c.rank) !== null).length;
    const oppTeams = getOpponentTeamIds(state, teamId);
    const oppHasCollectionCards = oppTeams.some((tid) => (state.collections[tid]?.cards?.length || 0) > 0);

    const targetBuildValue =
      move.type === ACTIONS.BUILD_CREATE
        ? ((move as any).buildValue as number)
        : move.type === ACTIONS.BUILD_UPDATE
          ? ((move as any).newValue as number)
          : move.type === ACTIONS.BUILD_SOLID_FROM_TABLE || move.type === ACTIONS.BUILD_SOLID_FROM_OPP_TOP
            ? (playedNumeric ?? 0)
            : 0;

    if (earlyGame && targetBuildValue >= 8 && targetBuildValue <= 10) {
      if (!oppHasCollectionCards && numericLoose < 2) {
        penalty += 85 * weights.avoidEarlyHighBuildMult;
      }
    }
  }

  // 0.5) Avoid taking a build/stay pile that has no "quality" cards (unless it's a large pile).
  if (
    (move.type === ACTIONS.TAKE_PILE || move.type === ACTIONS.TAKE_PILE_ADD_TO_BUILD || move.type === ACTIONS.TAKE_PILE_ADD_TO_STAY) &&
    next.phase === 'playing'
  ) {
    const anyMove = move as any;
    const target = anyMove.target || anyMove.takeTarget;
    const pile =
      target?.type === 'build'
        ? state.builds.find((b) => b.id === target.pileId)
        : target?.type === 'stay'
          ? state.stays.find((s) => s.id === target.pileId)
          : null;
    if (pile) {
      const cards = (pile as any).cards as Card[];
      const spadeCount = cards.filter((c) => c.suit === 'S').length;
      const hasQuality =
        cards.length >= 10 ||
        spadeCount >= 2 ||
        cards.some((c) => c.rank === 'A') ||
        cards.some((c) => c.rank === '10' && c.suit === 'D') ||
        cards.some((c) => c.rank === '2' && c.suit === 'S');
      if (!hasQuality) {
        penalty += 95 * weights.avoidLowValuePileTakeMult;
      }
    }
  }

  // 1) Avoid dropping high cards (7-10) unless forced.
  if (move.type === ACTIONS.DROP && playedNumeric !== null && playedNumeric >= 7 && playedNumeric <= 10) {
    penalty += (18 + (playedNumeric - 6) * 2) * weights.protectPointsMult;
    if (played.rank === '10') {
      // Tens are often high-value tactical cards; treat them as extra-sensitive.
      penalty += 28 * weights.protectPointsMult;
    }
  }

  // 2) Avoid dropping spades to keep spade-scoring potential.
  if (move.type === ACTIONS.DROP && isSpade(played)) {
    penalty += 10 * weights.protectPointsMult;
  }

  // 2.5) Avoid dropping Aces (worth points). Extra penalty if opponent can immediately capture it.
  if (move.type === ACTIONS.DROP && isAce(played)) {
    const canTake = opponentCanTakeDroppedCard(next, played.id, playerId);
    penalty += ((lateGame ? 55 : 38) + (lastRound ? 18 : 0) + (canTake ? 85 : 0)) * weights.protectPointsMult;
  }

  // 3) Protect 10♦: huge penalty if you drop it or use it without immediately securing it in collection.
  if (isTenDiamonds(played)) {
    const teamId = getPlayerTeamId(state, playerId);
    const teamCardsNext = next.collections[teamId]?.cards || [];
    const secured = teamCardsNext.some(isTenDiamonds);
    if (move.type === ACTIONS.DROP) penalty += 80 * weights.protectPointsMult;
    else if (!secured) penalty += 35 * weights.protectPointsMult;
  }

  // 4) Don't waste 2♠ (and generally avoid 2s unless needed).
  if (isTwoSpades(played)) {
    if (move.type === ACTIONS.DROP) penalty += 90 * weights.protectPointsMult;
    else penalty += 40 * weights.protectPointsMult;
  }

  if (move.type === ACTIONS.DROP && played.rank === '2') {
    penalty += 16 * weights.protectPointsMult;
  }

  // 4.5) Last-round endgame: avoid spending/dropping royals. Keeping at least one royal
  // increases the chance to take the final loose royal and become last collector for the sweep.
  if (isRoyal(played) && (lastRound || lateGame)) {
    const gained = findNewCollectionCards(state, next, teamId).length;
    const prevRoyalCount = (state.hands[playerId] || []).filter(isRoyal).length;
    const nextRoyalCount = (next.hands[playerId] || []).filter(isRoyal).length;
    const handAfter = (next.hands[playerId] || []).length;
    const canTake = move.type === ACTIONS.DROP ? opponentCanTakeDroppedCard(next, played.id, playerId) : false;
    const endedRoundOrGame = next.phase !== 'playing';

    if (move.type === ACTIONS.DROP) {
      penalty += ((lastRound ? 95 : 35) + (lateGame ? 20 : 0) + (canTake ? 65 : 0)) * weights.protectPointsMult;
    }

    // Don't "spend" a royal too early in the last round unless it immediately improves your collection
    // or ends the round/game (where being last collector matters most).
    if (endgame && !endedRoundOrGame && gained === 0 && handAfter > 0) {
      penalty += (28 + (handAfter <= 2 ? 18 : 0)) * weights.protectPointsMult;
    }

    const losingLastRoyal = prevRoyalCount > 0 && nextRoyalCount === 0 && handAfter > 0;
    if (losingLastRoyal && gained === 0) {
      penalty += (55 + (lastRound ? 25 : 0)) * weights.protectPointsMult;
    }

    // Save royals for the end: extra penalty if we spend a royal in the last round without ending our hand.
    if (lastRound && !endedRoundOrGame && handAfter > 0 && gained === 0) {
      penalty += 45 * weights.saveRoyalForLastMult;
    }

    // Mild bonus for finishing the last round on a royal (i.e., royal is your last card).
    if (lastRound && handAfter === 0 && gained === 0) {
      penalty -= 30 * weights.saveRoyalForLastMult;
    }
  }

  // 4.6) Prefer covering our own collection top with a royal when our top numeric could reinforce an opponent pile.
  // (We can do this by taking a loose royal with a matching royal in hand; the played royal becomes our new top.)
  {
    const top = state.collections[teamId]?.cards?.length ? state.collections[teamId].cards[state.collections[teamId].cards.length - 1] : null;
    const topV = top ? numericRankValue(top.rank) : null;
    if (topV !== null) {
      const oppTeamIds = getOpponentTeamIds(state, teamId);
      const oppHasAnyPile = state.builds.some((b) => oppTeamIds.includes(getPlayerTeamId(state, b.ownerPlayerId))) || state.stays.some((s) => oppTeamIds.includes(getPlayerTeamId(state, s.ownerPlayerId)));
      if (oppHasAnyPile) {
        const oppPlayers = state.players.filter((p) => oppTeamIds.includes(p.teamId)).map((p) => p.id);
        const oppHands = oppPlayers.flatMap((pid) => state.hands[pid] || []);
        const oppPiles = [
          ...state.builds.filter((b) => oppTeamIds.includes(getPlayerTeamId(state, b.ownerPlayerId))),
          ...state.stays.filter((s) => oppTeamIds.includes(getPlayerTeamId(state, s.ownerPlayerId))),
        ];
        const threatened = oppPiles.some((pile: any) => {
          const target = pile.value as number;
          const needed = target - topV;
          if (needed < 1 || needed > 10) return false;
          return oppHands.some((c) => numericRankValue(c.rank) === needed);
        });
        if (threatened) {
          const playedIsRoyal = isRoyal(played);
          if (move.type === ACTIONS.TAKE_SINGLE && playedIsRoyal) {
            penalty -= 55 * weights.preferRoyalCoverMult;
          } else if (takeAvailable && !playedIsRoyal) {
            penalty += 10 * weights.preferRoyalCoverMult;
          }
        }
      }
    }
  }

  // 4.75) Spending point cards without securing them: avoid using A/10/spades in builds/stays unless it gains collection now.
  {
    const teamId = getPlayerTeamId(state, playerId);
    const gained = findNewCollectionCards(state, next, teamId);
    const playedSecuredNow = gained.some((c) => c.id === played.id);
    if (!playedSecuredNow) {
      const spendPenalty =
        (isAce(played) ? 18 : 0) +
        (played.rank === '10' ? 14 : 0) +
        (isTwoSpades(played) ? 20 : 0) +
        (played.rank === '7' && isSpade(played) ? 16 : 0) +
        (isSpade(played) ? 6 : 0);
      if (spendPenalty > 0) {
        penalty += spendPenalty * (lateGame ? 1.5 : 1) * weights.protectPointsMult;
      }
    }
  }

  // 5) Drop safety: penalize drops that allow a strong immediate opponent reply.
  if (move.type === ACTIONS.DROP && next.phase === 'playing') {
    const baseAfterDrop = evaluateState(next, playerId, difficulty);
    const worstReply = worstOpponentReplyScoreForUs(next, playerId, difficulty, () => 0);
    if (worstReply !== null && worstReply < baseAfterDrop) {
      const delta = baseAfterDrop - worstReply;
      // Scale down the evaluation delta to a stable penalty band.
      penalty += Math.min(180, delta / 8) * weights.takeBiasMult;
    }
  }

  // 5.25) Don't feed opponent build/stay reinforcement: dropping a card that completes
  // (buildValue - opponentHandValue) lets them add-to-build with one card.
  if (move.type === ACTIONS.DROP && playedNumeric !== null) {
    const myTeamId = getPlayerTeamId(state, playerId);
    const opponentPlayerIds = state.players.filter((p) => p.teamId !== myTeamId).map((p) => p.id);
    const opponentHands = opponentPlayerIds.flatMap((pid) => state.hands[pid] || []);
    const opponentPiles = [
      ...state.builds.filter((b) => getPlayerTeamId(state, b.ownerPlayerId) !== myTeamId),
      ...state.stays.filter((s) => getPlayerTeamId(state, s.ownerPlayerId) !== myTeamId),
    ];

    let worstFeed = 0;
    for (const pile of opponentPiles) {
      const v = (pile as any).value as number;
      if (typeof v !== 'number' || v < 1 || v > 10) continue;
      const needed = v - playedNumeric;
      if (needed < 1 || needed > 10) continue;
      const oppNeedCount = countNumericValue(opponentHands, needed);
      if (oppNeedCount <= 0) continue;
      const special = v === 10 && needed === 1 ? 18 : 0; // ace-to-10 pattern
      const pileSize = (pile as any).cards?.length ? (pile as any).cards.length : 0;
      const base = 22 + Math.min(2, oppNeedCount) * 14 + special + Math.min(20, pileSize * 3);
      worstFeed = Math.max(worstFeed, base);
    }
    penalty += worstFeed * weights.feedBuildAvoidMult;
  }

  // 5.5) Build-bait rule: avoid creating/claiming a build value the opponent can take out immediately,
  // unless it's a deliberate bait where opponent has only one take-out card and we still hold another.
  // (In local mode we can see hands; in online the opponent hands are hidden and this becomes no-op.)
  const myTeamId = getPlayerTeamId(state, playerId);
  const opponentPlayerIds = state.players.filter((p) => p.teamId !== myTeamId).map((p) => p.id);
  const opponentHands = opponentPlayerIds.flatMap((pid) => state.hands[pid] || []);

  const buildsAfter = next.builds.filter((b) => b.ownerPlayerId === playerId);
  const buildsBeforeOwned = new Set(state.builds.filter((b) => b.ownerPlayerId === playerId).map((b) => b.id));
  const buildsBeforeById = new Map(state.builds.map((b) => [b.id, b] as const));

  const newlyOwnedBuilds = buildsAfter.filter((b) => {
    const prev = buildsBeforeById.get(b.id);
    if (!prev) return true;
    return prev.ownerPlayerId !== playerId || !buildsBeforeOwned.has(b.id);
  });

  if (newlyOwnedBuilds.length > 0) {
    const myHandAfter = next.hands[playerId] || [];
    for (const b of newlyOwnedBuilds) {
      const v = b.value;
      if (v < 1 || v > 10) continue;
      const oppCount = countNumericValue(opponentHands, v);
      if (oppCount <= 0) continue;

      const myRemaining = countNumericValue(myHandAfter, v);
      const startedWithAtLeastTwo = myRemaining >= 1; // since we usually spend one to build/update
      const tinyBuild = b.cards.length <= 3;
      const baitAllowed = oppCount === 1 && startedWithAtLeastTwo && tinyBuild;

      if (!baitAllowed) {
        // Very risky: opponent can immediately remove your build with minimal effort.
        penalty = Math.max(penalty, ((oppCount >= 2 ? 165 : 110) + b.cards.length * 4) * weights.feedBuildAvoidMult);
        continue;
      }

      // Bait is allowed but still not free: you are giving away cards to burn their only take-out.
      if (lastRound || lateGame) {
        penalty += 18 * weights.feedBuildAvoidMult;
        continue;
      }
      penalty += 32 * weights.feedBuildAvoidMult;
    }
  }

  // 6) Endgame royal-pair preservation: avoid breaking a royal pair if a top-pair capture opportunity exists.
  if (
    isRoyal(played) &&
    move.type !== ACTIONS.TAKE_ROYAL_FROM_COLLECTION &&
    move.type !== ACTIONS.REMOVE_ROYAL_COVER
  ) {
    const prevHand = state.hands[playerId] || [];
    const nextHand = next.hands[playerId] || [];
    const prevCount = countRoyalRank(prevHand, played.rank);
    const nextCount = countRoyalRank(nextHand, played.rank);
    const breaksPair = prevCount >= 2 && nextCount === 1;

    if (breaksPair && (lastRound || lateGame)) {
      const oppHasTopPair = hasOpponentTopRoyalPair(state, playerId, played.rank);
      if (oppHasTopPair) {
        penalty += 65 * weights.protectPointsMult;
      } else {
        penalty += 22 * weights.protectPointsMult;
      }
    }
  }

  // Small nudge: if this action spends a spade card and doesn't increase your collection, prefer a non-spade alternative.
  if (isSpade(played)) {
    const teamId = getPlayerTeamId(state, playerId);
    const gained = findNewCollectionCards(state, next, teamId).length;
    if (gained === 0) penalty += 3 * weights.protectPointsMult;
  }

  return penalty;
}

function moveBonuses(prev: GameState, next: GameState, perspectivePlayerId: string, difficulty: AiDifficulty): number {
  const teamId = getPerspectiveTeamId(prev, perspectivePlayerId);
  const gained = findNewCollectionCards(prev, next, teamId);
  if (gained.length === 0) return 0;

  const gainedSpades = gained.filter(isSpade).length;
  const gained10d = gained.some(isTenDiamonds) ? 1 : 0;
  const gainedAces = gained.filter((card) => card.rank === 'A').length;

  const remaining = totalRemainingCards(next);
  const lateGameFactor = remaining <= 10 ? 2 : remaining <= 20 ? 1.4 : 1;

  let bonus = gained.length * 3;
  bonus += gainedSpades * 4;
  bonus += gainedAces * 6;
  bonus += gained10d * 14;

  if (difficulty === 'HARD') {
    if (next.meta.lastCollectorTeamId === teamId) {
      bonus += 6 * lateGameFactor;
    }

    // If the move ends the last round, strongly reward being the last collector based on the
    // amount of sweepable table cards that would otherwise be lost.
    if (next.phase === 'gameOver' && prev.phase === 'playing' && isLastRound(prev) && next.meta.lastCollectorTeamId === teamId) {
      bonus += sweepableTableCount(prev) * 12;
      bonus += 35 * lateGameFactor;
    }

    // Last-round sweep pressure: being the last collector matters more when there's a pile
    // of cards still on the table (loose/builds/stays) and few turns remain.
    if (isLastRound(next) && totalRemainingCards(next) <= 10 && next.meta.lastCollectorTeamId === teamId) {
      bonus += sweepableTableCount(next) * 2.2 * lateGameFactor;
      if (prev.meta.lastCollectorTeamId !== teamId) {
        bonus += 10 * lateGameFactor;
      }
    }
  }

  return bonus;
}

function moveStrategicBonuses(
  prev: GameState,
  next: GameState,
  playerId: string,
  move: MoveOption,
  difficulty: AiDifficulty,
  weights: AiLearningWeights = DEFAULT_AI_WEIGHTS
): number {
  if (difficulty !== 'HARD') return 0;

  const myTeamId = getPlayerTeamId(prev, playerId);
  const myTeamPlayerIds = prev.players.filter((p) => p.teamId === myTeamId).map((p) => p.id);
  const oppPlayerIds = prev.players.filter((p) => p.teamId !== myTeamId).map((p) => p.id);
  const myTeamHand = myTeamPlayerIds.flatMap((pid) => prev.hands[pid] || []);
  const oppHand = oppPlayerIds.flatMap((pid) => prev.hands[pid] || []);

  const inferTargetValue = (): number | null => {
    const anyMove = move as Record<string, any>;
    if (move.type === ACTIONS.BUILD_CREATE && typeof anyMove.buildValue === 'number') return anyMove.buildValue;
    if (move.type === ACTIONS.BUILD_UPDATE && typeof anyMove.newValue === 'number') return anyMove.newValue;
    if (move.type === ACTIONS.ADD_TO_BUILD) {
      const pileId = String((anyMove.pileId as string) || '');
      const pile = prev.builds.find((b) => b.id === pileId);
      return pile?.value ?? null;
    }
    if (move.type === ACTIONS.BUILD_SOLID_FROM_TABLE || move.type === ACTIONS.BUILD_SOLID_FROM_OPP_TOP) {
      const played = getPlayedCardFromMove(prev, playerId, move);
      const v = played ? numericRankValue(played.rank) : null;
      return v && v >= 1 && v <= 10 ? v : null;
    }
    if (move.type === ACTIONS.REINFORCE_BUILD) {
      const playedId = (anyMove.cardIdFromHand as string) || '';
      const card = (prev.hands[playerId] || []).find((c) => c.id === playedId);
      const v = card ? numericRankValue(card.rank) : null;
      return v && v >= 1 && v <= 10 ? v : null;
    }
    if (move.type === ACTIONS.STAY_CREATE || move.type === ACTIONS.STAY_FROM_OPP_COLLECTION || move.type === ACTIONS.INCREASE_OPP_TOP_TO_MATCH) {
      const played = getPlayedCardFromMove(prev, playerId, move);
      const v = played ? numericRankValue(played.rank) : null;
      return v && v >= 1 && v <= 10 ? v : null;
    }
    if (move.type === ACTIONS.ADD_TO_STAY || move.type === ACTIONS.REINFORCE_STAY) {
      const pileId = String((anyMove.pileId as string) || '');
      const pile = prev.stays.find((s) => s.id === pileId);
      return pile?.value ?? null;
    }
    return null;
  };

  const v = inferTargetValue();
  if (!v || v < 1 || v > 10) return 0;

  const myCount = countNumericValue(myTeamHand, v);
  const oppCount = countNumericValue(oppHand, v);

  // Dominance detection: if we hold most copies (e.g., 3/4), we can bait the last one.
  const dominant = myCount >= 3 && oppCount >= 1;
  const opponentHasOnlyOne = oppCount === 1;

  let bonus = 0;
  if (dominant && opponentHasOnlyOne) {
    // Encourage creating/locking a dominant build/stay to bait the last copy out.
    if (
      move.type === ACTIONS.BUILD_CREATE ||
      move.type === ACTIONS.BUILD_UPDATE ||
      move.type === ACTIONS.ADD_TO_BUILD ||
      move.type === ACTIONS.BUILD_SOLID_FROM_TABLE ||
      move.type === ACTIONS.BUILD_SOLID_FROM_OPP_TOP ||
      move.type === ACTIONS.REINFORCE_BUILD
    ) {
      bonus += 26;
    }
    if (move.type === ACTIONS.STAY_CREATE || move.type === ACTIONS.ADD_TO_STAY || move.type === ACTIONS.REINFORCE_STAY) {
      bonus += 18;
    }
  }

  // Extra reward for locking/solidifying dominant values.
  if (dominant) {
    if (move.type === ACTIONS.BUILD_SOLID_FROM_TABLE || move.type === ACTIONS.BUILD_SOLID_FROM_OPP_TOP) {
      bonus += 22;
    }
    if (move.type === ACTIONS.REINFORCE_BUILD || move.type === ACTIONS.REINFORCE_STAY) {
      bonus += 16;
    }
  }

  // Reward growing a bait build (more cards pulled under one take-out later).
  if (dominant && move.type === ACTIONS.ADD_TO_BUILD) {
    const anyMove = move as Record<string, any>;
    const pileId = String((anyMove.pileId as string) || '');
    const before = prev.builds.find((b) => b.id === pileId);
    const after = next.builds.find((b) => b.id === pileId);
    if (before && after && after.cards.length > before.cards.length) {
      const added = after.cards.length - before.cards.length;
      bonus += Math.min(30, added * 6);
    }
  }

  return bonus * weights.dominanceBonusMult;
}

function scoreMove(
  prev: GameState,
  playerId: string,
  move: MoveOption,
  difficulty: AiDifficulty,
  preferNonSpadeShapes: Map<string, boolean>,
  weights: AiLearningWeights = DEFAULT_AI_WEIGHTS
): number {
  const next = applyAction(prev, move);
  const base = evaluateState(next, playerId, difficulty);
  const bonus = moveBonuses(prev, next, playerId, difficulty);
  const strategic = moveStrategicBonuses(prev, next, playerId, move, difficulty, weights);
  const penalty = moveSpecificPenalty(prev, playerId, move, next, difficulty, weights);
  const played = getPlayedCardFromMove(prev, playerId, move);
  const shapePenalty = played && isSpade(played) && preferNonSpadeShapes.get(moveShapeKey(move)) ? 12 : 0;
  return base + bonus + strategic - penalty - shapePenalty;
}

function chooseByHeuristic(
  state: GameState,
  playerId: string,
  moves: MoveOption[],
  rng: () => number,
  difficulty: AiDifficulty,
  weights: AiLearningWeights = DEFAULT_AI_WEIGHTS
): Action {
  const preferNonSpadeShapes = preferNonSpadeForEquivalentMoves(state, playerId, moves);
  const scored = moves.map((move) => {
    return { move, score: scoreMove(state, playerId, move, difficulty, preferNonSpadeShapes, weights) };
  });
  const bestScore = Math.max(...scored.map((entry) => entry.score));
  const best = scored.filter((entry) => entry.score === bestScore).map((entry) => entry.move);
  return pickRandom(best, rng);
}

function chooseTwoPly(
  state: GameState,
  playerId: string,
  moves: MoveOption[],
  rng: () => number,
  difficulty: AiDifficulty,
  weights: AiLearningWeights = DEFAULT_AI_WEIGHTS
): Action {
  if (state.mode !== '2p') {
    return chooseByHeuristic(state, playerId, moves, rng, difficulty, weights);
  }

  const preferNonSpadeShapes = preferNonSpadeForEquivalentMoves(state, playerId, moves);
  const scored = moves.map((move) => {
    const next = applyAction(state, move);
    if (next.phase !== 'playing') {
      return { move, score: scoreMove(state, playerId, move, difficulty, preferNonSpadeShapes, weights) };
    }

    const replyPlayerId = next.currentPlayerId;
    if (replyPlayerId === playerId) {
      return { move, score: scoreMove(state, playerId, move, difficulty, preferNonSpadeShapes, weights) };
    }

    const replyMoves = getLegalMoves(next, replyPlayerId);
    if (replyMoves.length === 0) {
      return { move, score: scoreMove(state, playerId, move, difficulty, preferNonSpadeShapes, weights) };
    }

    const nextAfterReplyScores = replyMoves.map((reply) => evaluateState(applyAction(next, reply), playerId, difficulty));
    const worstReplyScore = Math.min(...nextAfterReplyScores);
    const ownMoveScore = scoreMove(state, playerId, move, difficulty, preferNonSpadeShapes, weights);
    return { move, score: Math.min(ownMoveScore, worstReplyScore) };
  });

  const bestScore = Math.max(...scored.map((entry) => entry.score));
  const best = scored.filter((entry) => entry.score === bestScore).map((entry) => entry.move);
  return pickRandom(best, rng);
}

function topKMoves(
  state: GameState,
  playerId: string,
  moves: MoveOption[],
  difficulty: AiDifficulty,
  rng: () => number,
  limit: number,
  weights: AiLearningWeights = DEFAULT_AI_WEIGHTS
): MoveOption[] {
  if (moves.length <= limit) return moves;
  const preferNonSpadeShapes = preferNonSpadeForEquivalentMoves(state, playerId, moves);
  return moves
    .map((move) => ({ move, score: scoreMove(state, playerId, move, difficulty, preferNonSpadeShapes, weights) + rng() * 0.01 }))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((entry) => entry.move);
}

function chooseThreePly(
  state: GameState,
  playerId: string,
  moves: MoveOption[],
  rng: () => number,
  difficulty: AiDifficulty,
  weights: AiLearningWeights = DEFAULT_AI_WEIGHTS
): Action {
  if (state.mode !== '2p') {
    return chooseTwoPly(state, playerId, moves, rng, difficulty, weights);
  }

  const rootCandidates = topKMoves(state, playerId, moves, difficulty, rng, 12, weights);
  const rootPreferNonSpadeShapes = preferNonSpadeForEquivalentMoves(state, playerId, rootCandidates);

  const scored = rootCandidates.map((move) => {
    const next = applyAction(state, move);
    const rootEval = evaluateState(next, playerId, difficulty);
    const rootScored = scoreMove(state, playerId, move, difficulty, rootPreferNonSpadeShapes, weights);
    const rootDelta = rootScored - rootEval;

    if (next.phase !== 'playing') {
      return { move, score: rootEval + rootDelta };
    }

    const oppId = next.currentPlayerId;
    if (!oppId || oppId === playerId) {
      return { move, score: rootEval + rootDelta };
    }

    const oppMovesAll = getLegalMoves(next, oppId);
    if (oppMovesAll.length === 0) {
      return { move, score: rootEval + rootDelta };
    }

    // Opponent is assumed to pick the line that is worst for us.
    // Prune by considering only the opponent replies that look most damaging.
    const oppCandidates = oppMovesAll
      .map((oppMove) => ({ oppMove, score: evaluateState(applyAction(next, oppMove), playerId, difficulty) }))
      .sort((a, b) => a.score - b.score)
      .slice(0, 10)
      .map((entry) => entry.oppMove);

    const worstLineScore = Math.min(
      ...oppCandidates.map((oppMove) => {
        const afterOpp = applyAction(next, oppMove);
        if (afterOpp.phase !== 'playing') return evaluateState(afterOpp, playerId, difficulty) + rootDelta;
        if (afterOpp.currentPlayerId !== playerId) return evaluateState(afterOpp, playerId, difficulty) + rootDelta;

        const replyMovesAll = getLegalMoves(afterOpp, playerId);
        if (replyMovesAll.length === 0) return evaluateState(afterOpp, playerId, difficulty) + rootDelta - 650;
        const replyMoves = topKMoves(afterOpp, playerId, replyMovesAll, difficulty, rng, 10, weights);
        const replyPreferNonSpadeShapes = preferNonSpadeForEquivalentMoves(afterOpp, playerId, replyMoves);
        const bestReply = Math.max(
          ...replyMoves.map((reply) => scoreMove(afterOpp, playerId, reply, difficulty, replyPreferNonSpadeShapes, weights))
        );
        return bestReply + rootDelta;
      })
    );

    return { move, score: worstLineScore };
  });

  const bestScore = Math.max(...scored.map((entry) => entry.score));
  const best = scored.filter((entry) => entry.score === bestScore).map((entry) => entry.move);
  return pickRandom(best, rng);
}

function chooseAbsorptionAction(state: GameState, playerId: string, difficulty: AiDifficulty, rng: () => number): Action | null {
  const pending = state.turnContext.pendingAbsorption;
  if (!pending || pending.playerId !== playerId) return null;

  const choices = pending.choices || [];
  if (choices.length === 0) {
    return { type: ACTIONS.ABSORB_SKIP, playerId };
  }

  if (difficulty === 'EASY') {
    return { type: ACTIONS.ABSORB_CHOOSE, playerId, choiceId: pickRandom(choices, rng).id };
  }

  const scored = choices.map((choice) => {
    const action: Action = { type: ACTIONS.ABSORB_CHOOSE, playerId, choiceId: choice.id };
    const next = applyAction(state, action);
    return { action, score: evaluateState(next, playerId, difficulty) };
  });
  const bestScore = Math.max(...scored.map((entry) => entry.score));
  const best = scored.filter((entry) => entry.score === bestScore).map((entry) => entry.action);
  return pickRandom(best, rng);
}

export function chooseAiAction(
  state: GameState,
  playerId: string,
  difficulty: AiDifficulty,
  rng: () => number = Math.random,
  options?: ChooseAiOptions
): Action {
  const weights = difficulty === 'HARD' ? getActiveAiWeights() : DEFAULT_AI_WEIGHTS;
  const perfectInfo = Boolean(options?.perfectInfo);
  const fast = Boolean(options?.fast);

  const absorption = chooseAbsorptionAction(state, playerId, difficulty, rng);
  if (absorption) return absorption;

  const moves = getLegalMoves(state, playerId);
  if (moves.length === 0) {
    throw new Error(`No legal moves for ${playerId}`);
  }

  const hardDropFiltered =
    difficulty === 'HARD'
      ? (() => {
          const hasAnyTake = moves.some((m) => {
            return (
              m.type === ACTIONS.TAKE_SINGLE ||
              m.type === ACTIONS.TAKE_SUBSET ||
              m.type === ACTIONS.TAKE_PILE ||
              m.type === ACTIONS.TAKE_ROYAL_FROM_COLLECTION ||
              m.type === ACTIONS.REMOVE_ROYAL_COVER ||
              m.type === ACTIONS.STEAL_COLLECTION_TOP
            );
          });
          const hasNonDrop = moves.some((m) => m.type !== ACTIONS.DROP);
          const safeDropKeys = new Set(
            moves
              .filter((m) => m.type === ACTIONS.DROP)
              .filter((m) => {
                const played = getPlayedCardFromMove(state, playerId, m);
                return played ? !isValuableDropCard(played) : false;
              })
              .map((m) => stableMoveKey(m))
          );

          // If any safe drop exists, forbid dropping valuable cards.
          if (safeDropKeys.size > 0) {
            // If we can take anything at all, prefer taking (card advantage) over any drop.
            if (hasAnyTake) {
              return moves.filter((m) => m.type !== ACTIONS.DROP);
            }
            return moves.filter((m) => m.type !== ACTIONS.DROP || safeDropKeys.has(stableMoveKey(m)));
          }

          // If any non-drop move exists, forbid dropping valuable cards (only allow drops when forced).
          if (hasNonDrop) {
            return moves.filter((m) => m.type !== ACTIONS.DROP);
          }

          return moves;
        })()
      : moves;

  const filteredForHard =
    difficulty === 'HARD' && canAvoidTwoSpades(state, playerId, hardDropFiltered)
      ? hardDropFiltered.filter((move) => {
          const played = getPlayedCardFromMove(state, playerId, move);
          return !played || !isTwoSpades(played);
        })
      : hardDropFiltered;

  const shuffledMoves = filteredForHard
    .map((move) => ({ move, sort: rng() }))
    .sort((a, b) => a.sort - b.sort)
    .map((entry) => entry.move);

  if (difficulty === 'EASY') {
    return pickRandom(shuffledMoves, rng);
  }

  if (difficulty === 'HARD') {
    if (fast) {
      return chooseByHeuristic(state, playerId, shuffledMoves, rng, difficulty, weights);
    }
    if (perfectInfo && state.mode === '2p') {
      return chooseByPointRollouts(state, playerId, shuffledMoves, rng, difficulty, weights);
    }
    const preferNonSpadeShapes = preferNonSpadeForEquivalentMoves(state, playerId, shuffledMoves);
    const rootScored = shuffledMoves.map((move) => ({ move, score: scoreMove(state, playerId, move, difficulty, preferNonSpadeShapes, weights) }));
    const bestRoot = Math.max(...rootScored.map((entry) => entry.score));
    const window = 8;
    const candidates = rootScored.filter((entry) => entry.score >= bestRoot - window).map((entry) => entry.move);
    return chooseThreePly(state, playerId, candidates.length ? candidates : shuffledMoves, rng, difficulty, weights);
  }

  return chooseByHeuristic(state, playerId, shuffledMoves, rng, difficulty, weights);
}

export function isSameAction(a: Action, b: Action): boolean {
  return stableMoveKey(a) === stableMoveKey(b);
}
